import React, { ReactElement } from 'react';
import {
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  Button,
  ModalFooter,
  useDisclosure,
  useToast,
  Text,
  Link,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import { ToastMessage } from '@/screens/admin/types';
import { useAuthDetails } from '@/queries/useAuth';
import useLiveModeRequestSubmission, { LiveModeRequestFormData } from '../hooks/useLiveMSAFormSubmission';
import { formFields, getInitialValues, validationSchema } from '../helpers';
import FormField from './FormFields';
import SuccessModal from './SuccessModal';

const RequestLiveModeForm = ({
  isOpen: isRequestLiveFormOpen,
  onClose: closeRequestForm,
}: {
  isOpen: boolean;
  onClose: () => void;
}): ReactElement => {
  const toast = useToast();
  const { isOpen: isSuccessModalOpen, onOpen: onSuccessModalOpen, onClose: onSuccessModalClose } = useDisclosure();
  const { refetch: fetchAuthDetails } = useAuthDetails();
  const { mutateAsync: submitLiveModeRequest, isLoading } = useLiveModeRequestSubmission({
    onSuccess: () => {
      closeRequestForm();
      onSuccessModalOpen();
      fetchAuthDetails();
    },
    onError: () => {
      toast({
        title: ToastMessage.ERROR_MESSAGE,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const formik = useFormik<LiveModeRequestFormData>({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: (formData, { setSubmitting }) => {
      if (formik.isValid) {
        submitLiveModeRequest(formData);
      }

      setSubmitting(false);
    },
  });

  return (
    <>
      <Modal
        isOpen={isRequestLiveFormOpen}
        onClose={closeRequestForm}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
        scrollBehavior="inside"
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader p="6" pb="8px !important">
            <Flex direction="column" gridGap="2">
              <Heading as="h1" fontSize="md" fontWeight="medium" color="gray.800">
                Request to Live Mode Activation
              </Heading>
            </Flex>
            <ModalCloseButton top="5" right="6" />
          </ModalHeader>
          <form id="live-mode-msa-form" onSubmit={formik.handleSubmit}>
            <ModalBody h="full" p="6">
              <VStack spacing={4} align="stretch">
                {formFields.map(({ label, name, component, ...props }) => (
                  <FormField
                    key={name}
                    label={label}
                    name={name}
                    component={component}
                    value={formik.values[name] || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors[name]}
                    touched={formik.touched[name]}
                    {...props}
                  />
                ))}
                <Flex direction="column" gridGap="2" alignItems="flex-start" mt="2">
                  <Text fontSize="sm" textAlign="left">
                    Bureau&apos;s{' '}
                    <Link
                      href="https://www.bureau.id/privacy-policy"
                      color="blue.500"
                      isExternal
                      textDecoration="underline"
                    >
                      Privacy Policy
                    </Link>
                    {' and '}
                    <Link
                      href="https://www.bureau.id/terms-and-conditions"
                      color="blue.500"
                      isExternal
                      textDecoration="underline"
                    >
                      Terms and Conditions
                    </Link>
                    {' apply.'}
                  </Text>
                </Flex>
              </VStack>
            </ModalBody>
            <ModalFooter p="4" pt="8px !important" w="full">
              <Button
                variant="outline"
                fontSize="sm"
                fontWeight="300"
                mr="3"
                px="4"
                py="3"
                bg="white.50"
                onClick={closeRequestForm}
                _hover={{ bg: 'blue.50' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="live-mode-msa-form"
                fontSize="sm"
                fontWeight="300"
                px="4"
                py="3"
                colorScheme="blue"
                _hover={{ bg: 'blue.600' }}
                isLoading={isLoading}
              >
                Request
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={onSuccessModalClose}
        title="Live Mode Request Sent Successfully"
        subtitle="Our Sales team will review and approve your request shortly."
      />
    </>
  );
};

export default RequestLiveModeForm;
